// Short label summarizing a tool / team member's allocations, for display
// in the Tools matrix and OneOffsView "Assigned to" column.
//
// Examples:
//   "SEO" — single dept at 100%
//   "SEO 60% · Email 40%" — multiple allocations
//   "Agency" — agency scope
//   "—" — no allocations
export function allocationsSummary(entity, offers = []) {
  const allocs = entity?.offerAllocations || [];
  if (allocs.length === 0) return { label: "—", title: "No allocations set" };
  const parts = allocs.map((a) => {
    const scope = a.scope || (a.offerId ? "offer" : null);
    const v = Number(a.value) || 0;
    const valueLabel = a.mode === "amount" ? `$${v}` : `${v}%`;
    let name;
    if (scope === "offer") {
      const offer = offers.find((o) => o.id === a.offerId);
      name = offer?.name || "Offer";
    } else if (scope === "department") {
      name = a.scopeId || "Dept";
    } else if (scope === "agency") {
      name = "Agency";
    } else {
      name = "?";
    }
    return { name, valueLabel };
  });
  if (parts.length === 1 && (parts[0].valueLabel === "100%" || allocs[0]?.mode === "amount")) {
    return { label: parts[0].name, title: `${parts[0].name} · ${parts[0].valueLabel}` };
  }
  const label = parts.map((p) => `${p.name} ${p.valueLabel}`).join(" · ");
  return { label, title: label };
}

// Does this tool match the active dept + costType filters?
//   deptFilter:    "" (all) | "__agency__" | "__unassigned__" | "<DeptName>"
//   costTypeFilter: "" (all) | "cogs" | "opex"
// Returns true if at least one allocation matches the filter (OR semantics
// across allocations — a tool with both Email COGS and Agency OPEX matches
// any of: dept=Email, dept=Agency, type=COGS, type=OPEX).
export function toolMatchesFilters(entity, deptFilter, costTypeFilter, offers = []) {
  const allocs = entity?.offerAllocations || [];
  if (deptFilter === "__unassigned__") {
    // Unassigned = no allocations at all
    return allocs.length === 0;
  }
  if (allocs.length === 0) {
    return !deptFilter && !costTypeFilter;
  }
  return allocs.some((a) => {
    const scope = a.scope || (a.offerId ? "offer" : null);
    const raw = a.costType || "cogs";
    // Allow the filter to target the raw value (cogs / opex / marketing).
    if (costTypeFilter && raw !== costTypeFilter) return false;
    if (!deptFilter) return true;
    if (deptFilter === "__agency__") return scope === "agency";
    if (scope === "department") return a.scopeId === deptFilter;
    if (scope === "offer") {
      const offer = offers.find((o) => o.id === a.offerId);
      return (offer?.department || null) === deptFilter;
    }
    return false;
  });
}

// Whether a tool / team allocation is treated as COGS, OPEX, Marketing, mixed,
// or none. Drives the small badge next to "Assigned to" so you can tell at a
// glance whether the cost hits delivery margin or overhead.
//
// Marketing is a tagging-only flavor of OPEX: in the P&L waterfall, marketing
// costs still flow into OPEX (Tools overhead). The MARKETING badge just makes
// ad spend instantly identifiable on the Tools matrix.
//
// Returns { kind, label, title } where kind ∈ "cogs" | "opex" | "marketing" | "mixed" | "none".
export function costTypeSummary(entity) {
  const allocs = entity?.offerAllocations || [];
  if (allocs.length === 0) return { kind: "none", label: "—", title: "No allocations set" };
  let hasCogs = false;
  let hasOpex = false;
  let hasMarketing = false;
  for (const a of allocs) {
    const raw = a.costType || "cogs";
    if (raw === "cogs") hasCogs = true;
    else if (raw === "marketing") hasMarketing = true;
    else hasOpex = true;
  }
  const flavors = [hasCogs, hasOpex, hasMarketing].filter(Boolean).length;
  if (flavors > 1) return { kind: "mixed", label: "Mix", title: "Multiple cost types — see tool profile" };
  if (hasCogs) return { kind: "cogs", label: "COGS", title: "Counts as delivery cost (COGS)" };
  if (hasMarketing) return { kind: "marketing", label: "Marketing", title: "Marketing / acquisition cost (rolls into OPEX in P&L)" };
  return { kind: "opex", label: "OPEX", title: "Counts as overhead (OPEX)" };
}
